import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getUserBragsheet, updateBragsheet } from '../api/bragsheet';
import { useLocation } from 'react-router-dom';

const BragsheetContext = createContext();

// Helper function to generate unique ID
const generateId = () => {
    return 'gen_'+Date.now() + Math.random();
};

// Helper function to check if last item has empty title or description
const canAddNewItem = (items) => {
    if (!items || items.length === 0) return true;
    const lastItem = items[0];
    if (!lastItem.title || !lastItem.title.trim()) return false;
    if (!lastItem.description) return false;
    console.log(lastItem)
    // lastItem.description = lastItem.description.replace(/<\/?[^>]+>/g, '');
    if(!lastItem.description.length) return false
    return true;
};

export const BragsheetProvider = ({ children }) => {
    // Summary state
    const [summary, setSummary] = useState('');

    // Work Experience state
    const [workExperience, setWorkExperience] = useState([]);

    // Projects state
    const [projects, setProjects] = useState([]);

    // Skills state
    const [skills, setSkills] = useState([]);

    // Loading and error states
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const location = useLocation();


    // Load bragsheet data
    useEffect(() => {
        const loadBragsheet = async () => {

            setLoading(true);
            try {
                const { success, resp } = await getUserBragsheet();
                if (success) {
                    setSummary(resp.summary || '');
                    setWorkExperience(resp.work_experience || []);
                    setProjects(resp.projects || []);
                    setSkills(resp.skills || []);
                    setError('');
                } else {
                    setError(resp);
                }
            } catch (err) {
                setError(err.message || 'Failed to load bragsheet');
            } finally {
                setLoading(false);
            }
        }
        if(location.pathname === "/bragSheet"){
            loadBragsheet()
        }
    }, [location.pathname]);

    // Summary operations
    const updateSummary = useCallback((newSummary) => {
        setSummary(newSummary);
    }, []);

    // Work Experience operations
    const addWorkExperience = useCallback(() => {
        if (!canAddNewItem(workExperience)) {
            return false;
        }

        const newExperience = {
            id: generateId(),
            title: '',
            organization: '',
            start_date: '',
            end_date: '',
            description: '',
            type: 'WORK_EXPERIENCE'
        };

        setWorkExperience(prev => [newExperience, ...prev]);
        return true;
    }, [workExperience]);

    const updateWorkExperience = useCallback((id, field, value) => {
        setWorkExperience(prev => 
            prev.map(exp => 
                exp.id === id ? { ...exp, [field]: value } : exp
            )
        );
    }, []);

    const removeWorkExperience = useCallback((id) => {
        setWorkExperience(prev => prev.filter(exp => exp.id !== id));
    }, []);

    // Projects operations
    const addProject = useCallback(() => {
        if (!canAddNewItem(projects)) {
            return false;
        }

        const newProject = {
            id: generateId(),
            title: '',
            organization: '',
            start_date: '',
            end_date: '',
            description: '',
            type: 'PROJECTS'
        };

        setProjects(prev => [newProject, ...prev]);
        return true;
    }, [projects]);

    const updateProject = useCallback((id, field, value) => {
        setProjects(prev => 
            prev.map(project => 
                project.id === id ? { ...project, [field]: value } : project
            )
        );
    }, []);

    const removeProject = useCallback((id) => {
        setProjects(prev => prev.filter(project => project.id !== id));
    }, []);

    // Skills operations
    const addSkill = useCallback((skill) => {
        const trimmedSkill = skill.trim();
        if (trimmedSkill && !skills.includes(trimmedSkill)) {
            setSkills(prev => [...prev, trimmedSkill]);
            return true;
        }
        return false;
    }, [skills]);

    const removeSkill = useCallback((skillToRemove) => {
        setSkills(prev => prev.filter(skill => skill !== skillToRemove));
    }, []);

    const updateSkills = useCallback((newSkills) => {
        setSkills(newSkills);
    }, []);

    // Bulk operations
    const resetBragsheet = useCallback(() => {
        setSummary('');
        setWorkExperience([]);
        setProjects([]);
        setSkills([]);
        setError('');
    }, []);

    const getBragsheetData = useCallback(() => {
        return {
            summary,
            work_experience: workExperience,
            projects,
            skills
        };
    }, [summary, workExperience, projects, skills]);

    // Save operation (placeholder - implement based on your API)
    const saveBragsheet = useCallback(async () => {
        setLoading(true)
        const bragsheet =  getBragsheetData();

        const { success, resp } = await updateBragsheet(bragsheet)
        // if(success){

        // }
        if(!success){
            setError(resp)
        }
        setLoading(false)
        
    }, [getBragsheetData]);

    const contextValue = {
        // States
        summary,
        workExperience,
        projects,
        skills,
        loading,
        error,

        // Summary operations
        updateSummary,

        // Work Experience operations
        addWorkExperience,
        updateWorkExperience,
        removeWorkExperience,

        // Projects operations
        addProject,
        updateProject,
        removeProject,

        // Skills operations
        addSkill,
        removeSkill,
        updateSkills,

        // Bulk operations
        resetBragsheet,
        saveBragsheet,
        getBragsheetData,

        // Utility functions
        canAddNewItem: (items) => canAddNewItem(items),
        generateId
    };

    return (
        <BragsheetContext.Provider value={contextValue}>
            {children}
        </BragsheetContext.Provider>
    );
};

export const useBragsheet = () => {
    const context = useContext(BragsheetContext);
    if (!context) {
        throw new Error('useBragsheet must be used within a BragsheetProvider');
    }
    return context;
};

export default BragsheetContext;
